import { Route } from '@angular/router';
import { ShellComponent } from './shell/shell.component';
import { shellResolver } from './shell/shell.resolver';
import { giftCardsContentResolver } from './gift-cards/gift-cards-content.resolver';
import { contactUsContentResolver } from './contact-us/contact-us-content.resolver';
import { NgxsModule } from '@ngxs/store';
import { importProvidersFrom } from '@angular/core';
import { toolkitContentResolver } from './shared/components/toolkit-base/toolkit-content.resolver';
import { SharedCheckout } from './shared/share-checkout.state';
import { TicketingCalendarState } from './ticketing/ticketing-calendar/ticketing-calendar.state';
import { ticketingEventGuard } from './ticketing/ticketing-event.guard';
import { ticketingShowResolver } from './ticketing/ticketing-show.resolver';
import { ticketingQuantitiesContentResolver } from './ticketing/ticketing-quantity/ticketing-quantities-content.resolver';
import { clearOnLeaveGuard } from './ticketing/ticketing-seats/clear-on-leave.guard';
import { ticketingQuantitiesEventResolver } from './ticketing/ticketing-quantity/ticketing-quantities-event.resolver';
import { ticketingResolver } from './ticketing/ticketing.resolver';
import { ticketingSeatsGuard } from './ticketing/ticketing-seats/ticketing-seats.guard';
import { pressKitContentResolver } from './press-kit/press-kit-content.resolver';

export const appRoutes: Route[] = [
  {
    path: '',
    component: ShellComponent,
    resolve: { shellInfo: shellResolver },
    children: [
      {
        path: 'group-signup',
        loadComponent: () =>
          import('./group-signup/group-signup.component').then(
            (c) => c.GroupSignupComponent
          ),
        title: 'Group Signup | Sight & Sound Theatres',
      },
      {
        path: 'signup',
        loadComponent: () =>
          import('./signup/signup.component').then((c) => c.SignupComponent),
      },
      {
        path: 'contact-us',
        resolve: { contactUsContent: contactUsContentResolver },
        loadComponent: () =>
          import('./contact-us/contact-us.component').then(
            (c) => c.ContactUsComponent
          ),

        title: 'Contact Us | Sight & Sound Theatres',
      },
      {
        path: 'media-toolkit',
        resolve: {
          toolkitContent: toolkitContentResolver,
        },
        data: {
          toolkitType: 'media',
        },
        loadComponent: () =>
          import('./media-toolkit/media-toolkit.component').then(
            (c) => c.MediaToolkitComponent
          ),

        title: 'Media Toolkit | Sight & Sound Theatres',
      },
      {
        path: 'partner-toolkit',
        resolve: {
          toolkitContent: toolkitContentResolver,
        },
        data: {
          // This toolkit type needs to stay reseller so that
          // the existing Directus query still works.
          // The directus collection name is "Reseller Toolkit"
          toolkitType: 'reseller',
        },
        loadComponent: () =>
          import('./partner-toolkit/partner-toolkit.component').then(
            (c) => c.PartnerToolkitComponent
          ),

        title: 'Business Partner Toolkit | Sight & Sound Theatres',
      },
      {
        path: 'press-kit',
        resolve: {
          toolkitContent: pressKitContentResolver,
        },
        data: {
          toolkitType: 'press',
        },
        loadComponent: () =>
          import('./press-kit/press-kit.component').then(
            (c) => c.PressKitComponent
          ),

        title: 'Press Kit | Sight & Sound Theatres',
      },
      {
        path: 'brochures',
        loadComponent: () =>
          import('./brochures/brochures.component').then(
            (c) => c.BrochuresComponent
          ),

        title: 'Brochures | Sight & Sound Theatres',
      },
      {
        path: 'gift-cards',
        resolve: { giftCardsContent: giftCardsContentResolver },
        title: 'Gift Cards | Sight & Sound Theatres',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./gift-cards/gift-cards.component').then(
                (c) => c.GiftCardsComponent
              ),
          },
          {
            path: 'check-balance',
            title: 'Check Your Gift Card Balance | Sight & Sound Theatres',
            loadComponent: () =>
              import('./gift-cards/gift-card-check-balance/gift-card-check-balance.component').then(
                (c) => c.GiftCardCheckBalanceComponent
              ),
          },
          {
            path: 'cart',
            loadComponent: () =>
              import('./gift-cards/gift-card-cart/gift-card-cart.component').then(
                (c) => c.GiftCardCartComponent
              ),
          },
          {
            path: 'checkout',
            loadComponent: () =>
              import('./gift-cards/gift-card-checkout/gift-card-checkout.component').then(
                (c) => c.GiftCardCheckoutComponent
              ),
            providers: [
              importProvidersFrom(
                NgxsModule.forFeature([SharedCheckout.SharedCheckoutState])
              ),
            ],
          },
          {
            path: 'confirmation',
            loadComponent: () =>
              import('./gift-cards/gift-card-confirmation/gift-card-confirmation.component').then(
                (c) => c.GiftCardConfirmationComponent
              ),
          },
        ],
      },
      {
        path: 'ticketing/tickets',
        loadComponent: () =>
          import('./redirect/redirect-tickets.component').then(
            (c) => c.RedirectTicketsComponent
          ),
      },
      {
        path: 'ticketing/statements',
        loadComponent: () =>
          import('./redirect/redirect-tickets.component').then(
            (c) => c.RedirectTicketsComponent
          ),
      },
      {
        path: 'ticketing/printable-seat-chart',
        loadComponent: () =>
          import('./redirect/redirect-tickets.component').then(
            (c) => c.RedirectTicketsComponent
          ),
      },
      {
        path: 'ticketing/confirmation',
        loadComponent: () =>
          import('./ticketing/ticketing-confirmation/ticketing-confirmation.component').then(
            (c) => c.TicketingConfirmationComponent
          ),
      },
      {
        path: 'ticketing',
        loadComponent: () =>
          import('./ticketing/ticketing.component').then(
            (c) => c.TicketingComponent
          ),
        providers: [
          importProvidersFrom(NgxsModule.forFeature([TicketingCalendarState])),
        ],
        children: [
          {
            path: '',
            resolve: { showsByLocationAndYear: ticketingResolver },
            title: 'Pick Location & Show | Sight & Sound Theatres',
            loadComponent: () =>
              import('./ticketing/ticketing-show/ticketing-show.component').then(
                (c) => c.TicketingShowComponent
              ),
          },
          {
            path: 'cart',
            title: 'Tickets Cart | Sight & Sound Theatres',
            loadComponent: () =>
              import('./ticketing/ticketing-cart/ticketing-cart.component').then(
                (c) => c.TicketingCartComponent
              ),
          },
          {
            path: 'checkout',
            title: 'Tickets Checkout | Sight & Sound Theatres',
            providers: [
              importProvidersFrom(
                NgxsModule.forFeature([SharedCheckout.SharedCheckoutState])
              ),
            ],
            loadComponent: () =>
              import('./ticketing/ticketing-checkout/ticketing-checkout.component').then(
                (c) => c.TicketingCheckoutComponent
              ),
          },
          {
            path: ':eventCode',
            resolve: {
              show: ticketingShowResolver,
            },
            children: [
              {
                path: 'calendar',
                loadComponent: () =>
                  import('./ticketing/ticketing-calendar/ticketing-calendar.component').then(
                    (c) => c.TicketingCalendarComponent
                  ),
              },
              {
                path: ':dateTime',
                canActivate: [ticketingEventGuard],
                children: [
                  {
                    path: '',
                    resolve: {
                      ticketQuantitiesContent:
                        ticketingQuantitiesContentResolver,
                      eventDetails: ticketingQuantitiesEventResolver,
                    },
                    loadComponent: () =>
                      import('./ticketing/ticketing-quantity/ticketing-quantity.component').then(
                        (c) => c.TicketingQuantityComponent
                      ),
                  },
                  {
                    path: 'seats',
                    canActivate: [ticketingSeatsGuard],
                    resolve: {
                      ticketQuantitiesContent:
                        ticketingQuantitiesContentResolver,
                    },
                    loadComponent: () =>
                      import('./ticketing/ticketing-seats/ticketing-seats.component').then(
                        (c) => c.TicketingSeatsComponent
                      ),
                    canDeactivate: [clearOnLeaveGuard],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'not-found',
        loadComponent: () =>
          import('./not-found/not-found.component').then(
            (c) => c.NotFoundComponent
          ),
        title: '404 | Sight & Sound Theatres',
      },
      // Component-based wildcard (no `redirectTo`). A `redirectTo` here would
      // be baked into `angular-app-manifest.mjs` at build time and trigger a
      // 302 inside `@angular/ssr`'s `ServerRouter` BEFORE Angular bootstraps,
      // which prevents `generateDynamicRoutes` from ever running for new CMS
      // pages. Rendering a component in place lets the server bootstrap
      // Angular, run the dynamic-routes initializer, and either match the URL
      // against a newly-registered CMS route or fall through to this 404.
      {
        path: '**',
        loadComponent: () =>
          import('./not-found/not-found.component').then(
            (c) => c.NotFoundComponent
          ),
        title: '404 | Sight & Sound Theatres',
      },
    ],
  },
];
